import { useState } from "react";
import { View, Text, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView, FlatList, TextInput, Image } from "react-native";
import { Link } from "expo-router";
import { useAuth } from "../lib/auth";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";

const LOGO = require("../../assets/images/logo-main.png");

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

export default function RegisterScreen() {
  const { signUp } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [showStates, setShowStates] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleRegister() {
    if (!name || !email || !password) {
      setError("Please fill in all fields");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await signUp(email, password, name, state || undefined, zipCode || undefined);
    } catch (err: any) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={{ flex: 1, backgroundColor: "#f9fafb" }}
    >
      <ScrollView contentContainerStyle={{ flexGrow: 1, justifyContent: "center", padding: 24 }}>
        <View style={{ marginBottom: 32, alignItems: "center" }}>
          <Image source={LOGO} style={{ width: 180, height: 90, marginBottom: 8 }} resizeMode="contain" />
          <Text style={{ fontSize: 16, color: "#6b7280", marginTop: 4 }}>Create your account</Text>
        </View>

        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        <Input label="Name" value={name} onChangeText={setName} placeholder="Your name" />
        <Input
          label="Email"
          value={email}
          onChangeText={setEmail}
          placeholder="you@example.com"
          keyboardType="email-address"
          autoCapitalize="none"
        />
        <Input
          label="Password"
          value={password}
          onChangeText={setPassword}
          placeholder="Choose a password"
          secureTextEntry
        />

        {/* State picker */}
        <View style={{ marginBottom: 16, zIndex: 10 }}>
          <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 6 }}>State (optional)</Text>
          <TouchableOpacity
            onPress={() => setShowStates(!showStates)}
            style={{
              borderWidth: 1,
              borderColor: showStates ? "#1B2A4A" : "#d1d5db",
              borderRadius: 10,
              paddingVertical: 12,
              paddingHorizontal: 16,
              backgroundColor: "#fff",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 16, color: state ? "#111827" : "#9ca3af" }}>
              {state || "Select state..."}
            </Text>
            <Text style={{ color: "#6b7280" }}>{showStates ? "▲" : "▼"}</Text>
          </TouchableOpacity>
          {showStates && (
            <View style={{ borderWidth: 1, borderColor: "#d1d5db", borderRadius: 10, backgroundColor: "#fff", marginTop: 4, maxHeight: 250, overflow: "hidden" }}>
              <TextInput
                value={stateSearch}
                onChangeText={setStateSearch}
                placeholder="Search states..."
                autoFocus
                placeholderTextColor="#9ca3af"
                style={{ borderBottomWidth: 1, borderBottomColor: "#e5e7eb", paddingVertical: 10, paddingHorizontal: 14, fontSize: 15, color: "#111827" }}
              />
              <FlatList
                data={US_STATES.filter((s) => !stateSearch || s.toLowerCase().includes(stateSearch.toLowerCase()))}
                keyExtractor={(item) => item}
                keyboardShouldPersistTaps="handled"
                style={{ maxHeight: 200 }}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    onPress={() => { setState(item); setShowStates(false); setStateSearch(""); }}
                    style={{
                      paddingVertical: 11,
                      paddingHorizontal: 14,
                      backgroundColor: item === state ? "#F0F2F5" : "#fff",
                      borderBottomWidth: 1,
                      borderBottomColor: "#f3f4f6",
                    }}
                  >
                    <Text style={{ fontSize: 15, color: item === state ? "#1B2A4A" : "#111827", fontWeight: item === state ? "600" : "400" }}>{item}</Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}
        </View>

        <Input
          label="Zip Code (optional)"
          value={zipCode}
          onChangeText={setZipCode}
          placeholder="e.g. 22032"
          keyboardType="number-pad"
        />

        <Button title="Create Account" onPress={handleRegister} loading={loading} />

        <View style={{ marginTop: 24, alignItems: "center" }}>
          <Link href="/(auth)/login/" asChild>
            <TouchableOpacity>
              <Text style={{ color: "#2563eb", fontSize: 14 }}>
                Already have an account? <Text style={{ fontWeight: "600" }}>Sign in</Text>
              </Text>
            </TouchableOpacity>
          </Link>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
